import { DataType } from "shared/types";

import { CountTypeEnum } from "pages/Dashboard/components/constants";
import { SparklineChart } from "pages/Dashboard/components/SmartGroupsTable/SparklineChart";

import Checkbox from "features/components/Checkbox";
import Tooltip from "features/components/Tooltip";

import {
  ColumnCellRenderProps,
  ColumnHeaderRenderProps,
  SmartGroupColumnDefinition,
  SmartGroupColumnId,
} from "./types";

export const chartColumn: SmartGroupColumnDefinition = {
  id: SmartGroupColumnId.CHART,

  getSchema: ({ chartKey }) => ({
    accessor: chartKey,
    label: "",
    dataType: DataType.JSX,
    columnWidth: 320,
  }),

  renderHeader: ({ context }: ColumnHeaderRenderProps) => {
    const {
      chartLabel,
      countType,
      eventsPeriod,
      isEventDate,
      vehiclesPeriod,
      showOnlyEventDateData,
      onToggleShowOnlyEventDateData,
      isSimplified,
      chartTimeGrouping,
      addProductionSuffix,
      t,
    } = context;

    const headerLabel = isEventDate
      ? t("schema:dashboard.chart_by_occurrence", {
          chartLabel,
          grouping: chartTimeGrouping,
        })
      : addProductionSuffix
        ? t("schema:dashboard.chart_last_period_of_production", {
            chartLabel,
            period: vehiclesPeriod,
          })
        : t("schema:dashboard.chart_last_period", {
            chartLabel,
            period: vehiclesPeriod,
          });

    const onlyShowText = t("schema:dashboard.only_show_title_for_date", {
      title: chartLabel,
      date: eventsPeriod,
    });

    const hoverText = isSimplified ? (
      t("schema:dashboard.chart_for_period", {
        chartLabel,
        period: eventsPeriod,
      })
    ) : countType === CountTypeEnum.FRC_IMPACT ? (
      t("schema:dashboard.only_show_affected_produced", {
        period: eventsPeriod,
      })
    ) : (
      <div
        style={{ whiteSpace: "pre-line" }}
      >{`${headerLabel}\n${onlyShowText}`}</div>
    );

    return (
      <Tooltip title={hoverText}>
        <div
          style={{
            whiteSpace: "pre-line",
            lineHeight: "19px",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          <div>{headerLabel}</div>
          <div className="text-gray-500 flex items-center overflow-hidden">
            <Checkbox
              checked={showOnlyEventDateData}
              onChange={onToggleShowOnlyEventDateData}
              disabled={isSimplified}
              sx={{
                p: 0,
                mr: 0.5,
                ml: "-1.4px",
                flexShrink: 0,
                "& .MuiSvgIcon-root": { fontSize: "16px" },
                "&.Mui-disabled": {
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            />
            <span
              className="whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer"
              onClick={onToggleShowOnlyEventDateData}
            >
              {onlyShowText}
            </span>
          </div>
        </div>
      </Tooltip>
    );
  },

  renderCell: ({ row, context }: ColumnCellRenderProps) => {
    const { eventType, countType, hoveredRowId, showOnlyEventDateData } =
      context;

    return (
      <SparklineChart
        key={`${row.groupID}-${showOnlyEventDateData}-${countType}`}
        filteredChartData={row.filteredChart || []}
        allTimeChartData={row.allTimeChart || []}
        eventType={eventType}
        countType={countType}
        isRowHovered={hoveredRowId === row.groupName}
        showOnlyEventDateData={showOnlyEventDateData}
      />
    );
  },
};
